import { CURRENCIES_FILED } from "../constants/currencies.constant";

export type PayoutCurrencyTotals = {
  available_payout: number;
  total_payout_amount: number;
  /** Value intended for UI display: organizer commission when customer-paid fees exist, otherwise the customer revenue. */
  display_total_revenue?: number;
  total_customer_revenue: number;
  total_commission: number;
  organizer_earnings: number;
  platform_commission: number;
  tax_on_platform_commission: number;
  organizer_charge: number;
  tax_on_organizer_charge: number;
  total_platform_cut: number;
  total_organizer_charge_cut: number;
  total_fee_deduction: number;
  total_platform_deduction: number;
  total_refund: number;
  already_requested: number;
  pending_payout?: number;
  tax_percentage?: number;
  tax_type?: string;
};

export const toPayoutNumber = (value: number | string | undefined | null) => {
  if (value === undefined || value === null || value === "") return 0;
  const n = typeof value === "number" ? value : parseFloat(String(value));
  return Number.isFinite(n) ? n : 0;
};

export function normalizePayoutCurrencyTotals(
  raw: Record<string, unknown>,
): PayoutCurrencyTotals {
  return {
    available_payout: toPayoutNumber(raw.available_payout as number | string),
    total_payout_amount: toPayoutNumber(
      raw.total_payout_amount as number | string,
    ),
    total_customer_revenue: toPayoutNumber(
      raw.total_customer_revenue as number | string,
    ),
    display_total_revenue: toPayoutNumber(
      raw.display_total_revenue as number | string,
    ),
    total_commission: toPayoutNumber(raw.total_commission as number | string),
    organizer_earnings: toPayoutNumber(
      raw.organizer_earnings as number | string,
    ) || toPayoutNumber(raw.total_commission as number | string),
    platform_commission: toPayoutNumber(
      raw.platform_commission as number | string,
    ),
    tax_on_platform_commission: toPayoutNumber(
      raw.tax_on_platform_commission as number | string,
    ),
    organizer_charge: toPayoutNumber(raw.organizer_charge as number | string),
    tax_on_organizer_charge: toPayoutNumber(
      raw.tax_on_organizer_charge as number | string,
    ),
    total_platform_cut: toPayoutNumber(raw.total_platform_cut as number | string),
    total_organizer_charge_cut: toPayoutNumber(
      raw.total_organizer_charge_cut as number | string,
    ),
    total_fee_deduction: toPayoutNumber(
      raw.total_fee_deduction as number | string,
    ),
    total_platform_deduction: toPayoutNumber(
      raw.total_platform_deduction as number | string,
    ),
    total_refund: toPayoutNumber(raw.total_refund as number | string),
    already_requested: toPayoutNumber(raw.already_requested as number | string),
    pending_payout: toPayoutNumber(raw.pending_payout as number | string),
    tax_percentage:
      raw.tax_percentage !== undefined && raw.tax_percentage !== null
        ? toPayoutNumber(raw.tax_percentage as number | string)
        : undefined,
    tax_type: typeof raw.tax_type === "string" ? raw.tax_type : undefined,
  };
}

export const formatPayoutAmount = (currency: string, value: number) => {
  const config = CURRENCIES_FILED[currency as keyof typeof CURRENCIES_FILED];
  if (!config) return `${currency} ${value.toFixed(2)}`;
  const formatted = value.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return config.prefix
    ? `${config.symbol} ${formatted}`
    : `${formatted} ${config.symbol}`;
};

export const getTaxLabel = (totals: PayoutCurrencyTotals, prefix = "Tax") => {
  if (totals.tax_type && totals.tax_percentage) {
    return `${prefix} GST (${totals.tax_percentage}%)`;
  }
  if (totals.tax_type) return `${prefix} (${totals.tax_type})`;
  return prefix === "Tax" ? "Tax" : `${prefix} Tax`;
};

export function isPayoutRejected(status?: string): boolean {
  return status?.toLowerCase() === "rejected";
}

/** Admin note on a payout request (`admin_note` from API). */
export function getPayoutAdminNote(
  record: Record<string, unknown>,
): string | null {
  const value = record.admin_note;
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }
  return null;
}

/** Shown when payout status is rejected and `admin_note` is present. */
export function getPayoutRejectionReason(
  record: Record<string, unknown>,
): string | null {
  if (!isPayoutRejected(record.status as string | undefined)) {
    return null;
  }
  return getPayoutAdminNote(record);
}

export const payoutStatusColor: Record<string, string> = {
  pending: "gold",
  approved: "green",
  completed: "green",
  rejected: "red",
  failed: "red",
  processing: "blue",
};
